import React from 'react';
import { format, isValid, parseISO } from 'date-fns';
import { uk } from 'date-fns/locale';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import './date-input.scss';

type BlurEventLike = {
   target: {
      name: string;
   };
};

type ChangeEventLike = {
   target: {
      name: string;
      value: string;
   };
};

export interface DateInputProps {
   name: string;
   value?: string | null;
   maxDate?: string;
   minDate?: string;
   format?: string;
   touched?: boolean;
   required?: boolean;
   error?: React.ReactNode;
   handleBlur?: (event: BlurEventLike) => void;
   handleChange: (event: ChangeEventLike) => void;
}

const parseDate = (date?: string): Date | undefined => {
   if (!date) {
      return undefined;
   }

   const parsedDate = parseISO(date);
   return isValid(parsedDate) ? parsedDate : undefined;
};

export const DateInput: React.FC<DateInputProps> = ({
   name,
   error,
   value,
   maxDate,
   minDate,
   format: dateFormat = 'yyyy-MM-dd',
   touched,
   required,
   handleBlur,
   handleChange,
}) => (
   <div className='date-input'>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={uk}>
         <DatePicker
            format={dateFormat}
            maxDate={parseDate(maxDate)}
            minDate={parseDate(minDate)}
            value={value ? parseDate(value) ?? null : null}
            onClose={() => handleBlur?.({ target: { name } })}
            slotProps={{
               textField: {
                  className: 'date-input__field',
                  error: Boolean(touched && error),
               },
               openPickerButton: {
                  className: 'date-input__open-picker-button',
               },
            }}
            onChange={(newValue, context) => {
               if (context.validationError) {
                  return;
               }

               handleChange({
                  target: {
                     name,
                     value: newValue ? format(newValue, 'yyyy-MM-dd') : '',
                  },
               });
            }}
         />
      </LocalizationProvider>
      {touched && error && <div className='text-danger'>{error}</div>}
   </div>
);
